import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { BdsBanner, BdsGrid, BdsSidebar, BdsTypo, BdsUpload } from 'blip-ds/dist/blip-ds-react';
import { useContentLocalizer } from '../../../../hooks/useContentLocalizer';
import { localization } from './localization';
import { ModalFooter } from 'components/ModalFooter';
import { useCommon } from 'contexts/CommonContext';
import { getLanguageCode } from 'utils/language';
import { languages } from 'blip-ds/dist/types/utils/languages';
import { ACCEPTED_FILE_TYPES } from 'views/KnowledgeBase/constants';
import PageHeader from 'components/PageHeader';
import { DocumentAccordion } from './components/DocumentAccordion';
import { FooterWrapper, UploadWrapper } from './styles';
import { PreProcessConfig } from 'models/knowledgeBase/Document';
import { KnowledgeBaseApi, UploadError } from 'api/KnowledgeBaseApi';
import { KNOWLEDGE_CONTENT_REFRESH, KNOWLEDGE_CONTENT_SAVE } from 'shared/SegmentEvents';
import { showToast } from 'api/CommonApi';
import { useKnowledgeBaseTracking } from 'hooks/useKnowledgeBaseTracking';
import FeatureToggleService from 'services/FeatureToggleService';
import { STUDIO_GROUNDING_PREPROCESSING_ENABLED } from 'shared/FeatureToggleKeys';

interface UploadFileSidebarProps {
    isOpen: boolean;
    tenantId: string;
    catalogId: string;
    documentToUpdateId?: string;
    onClose: (wasCancelled: boolean) => void;
}

const UploadFileSidebar: React.FC<UploadFileSidebarProps> = ({ isOpen, tenantId, catalogId, documentToUpdateId, onClose }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isGroundingPreprocessingEnabled, setIsGroundingPreprocessingEnabled] = useState<boolean>(false);
    const [preProcessConfig, setPreProcessConfig] = useState<PreProcessConfig[]>([]);
    const translate = useContentLocalizer(localization);
    const {
        common: { language },
    } = useCommon();
    const { trackKnowledgeBaseEvent } = useKnowledgeBaseTracking();
    const uploadRef = useRef<HTMLBdsUploadElement>(null);
    const bdsUploadFilesRef = useRef<File[]>([]);
    const pendingDeleteEvents = useRef(0);

    useEffect(() => {
        window.dispatchEvent(new CustomEvent('sidebar:toggle', { detail: { open: isOpen } }));
        return () => {
            if (isOpen) {
                window.dispatchEvent(new CustomEvent('sidebar:toggle', { detail: { open: false } }));
            }
        };
    }, [isOpen]);

    useEffect(() => {
        const checkIsGroundingPreprocessingEnabled = async () => {
            try {
                const featureEnabled = await FeatureToggleService.isFeatureToggleEnabled(STUDIO_GROUNDING_PREPROCESSING_ENABLED);
                setIsGroundingPreprocessingEnabled(featureEnabled);
            } catch {
                setIsGroundingPreprocessingEnabled(false);
            }
        };

        checkIsGroundingPreprocessingEnabled();
    }, []);

    // Memoize language code calculation
    const languageCode = useMemo(() => {
        return getLanguageCode(language) as languages;
    }, [language]);

    const resetForm = useCallback(() => {
        setPreProcessConfig([]);
        bdsUploadFilesRef.current = [];
        resetNativeFileInput();
        uploadRef.current?.deleteAllFiles();
        setIsLoading(false);
    }, []);

    const handleSidebarClose = useCallback(
        (event: CustomEvent) => {
            if (!event.detail.value) {
                resetForm();
                onClose(true);
            }
        },
        [onClose],
    );

    const handleFileUpload = useCallback((event: CustomEvent) => {
        if (pendingDeleteEvents.current > 0) {
            pendingDeleteEvents.current--;
            return;
        }
        const uploadedFiles = event.detail.value as File[];
        bdsUploadFilesRef.current = uploadedFiles;
        setPreProcessConfig(prevConfig => {
            const existingNames = new Set(prevConfig.map(doc => doc.file.name));
            const newFiles = uploadedFiles.filter(file => !existingNames.has(file.name));
            if (newFiles.length === 0) return prevConfig;
            return [
                ...prevConfig,
                ...newFiles.map(file => ({
                    file,
                    optimization: false,
                    indexing: false,
                    contextualization: false,
                })),
            ];
        });
    }, []);

    const resetNativeFileInput = () => {
        const inputElement = uploadRef.current?.shadowRoot?.querySelector('input[type="file"]') as HTMLInputElement | null;
        if (inputElement) {
            inputElement.value = '';
        }
    };

    const removeFileFromNativeInput = (deletedFileName: string) => {
        const inputElement = uploadRef.current?.shadowRoot?.querySelector('input[type="file"]') as HTMLInputElement | null;
        if (!inputElement?.files) return;
        const dt = new DataTransfer();
        Array.from(inputElement.files).forEach(file => {
            if (file.name !== deletedFileName) {
                dt.items.add(file);
            }
        });
        inputElement.files = dt.files;
    };

    const handleDeleteDocument = (fileName: string) => {
        if (isLoading) {
            return;
        }

        const indices = bdsUploadFilesRef.current
            .reduce<number[]>((acc, file, i) => (file.name === fileName ? [...acc, i] : acc), [])
            .reverse();
        pendingDeleteEvents.current += indices.length;
        indices.forEach(i => uploadRef.current?.deleteFile(i));

        bdsUploadFilesRef.current = bdsUploadFilesRef.current.filter(f => f.name !== fileName);
        setPreProcessConfig(prevFiles => prevFiles.filter(doc => doc.file.name !== fileName));
        removeFileFromNativeInput(fileName);
    };

    const handleTogglePreProcess = (fileName: string, type: 'optimization' | 'indexing' | 'contextualization', value: boolean) => {
        if (isLoading) {
            return;
        }

        setPreProcessConfig(prevFiles =>
            prevFiles.map(doc => {
                if (doc.file.name === fileName) {
                    return { ...doc, [type]: value };
                }
                return doc;
            })
        );
    };

    const handleCancel = () => {
        if (isLoading) {
            return;
        }

        resetForm();
        onClose(true);
    };

    const handleImportFiles = useCallback(async () => {
        if (preProcessConfig.length === 0 || isLoading) {
            return;
        }

        try {
            setIsLoading(true);
            const { documents, failedFiles } = await KnowledgeBaseApi.createPreprocessedDocuments(tenantId, catalogId, preProcessConfig);

            documents.forEach(doc => {
                const config = preProcessConfig.find(c => c.file.name === doc.name);
                trackKnowledgeBaseEvent(KNOWLEDGE_CONTENT_SAVE, {
                    knowcontName: config?.file.name,
                    knowcontType: config?.file.name.split('.').pop() ?? 'unknown',
                    knowcontSize: config?.file.size,
                    knowcontPreProcessingOptions: {
                        optimization: config?.optimization,
                        indexing: config?.indexing,
                        contextualization: config?.contextualization,
                    },
                });
            });

            showToast({
                title: translate.successImportFilesToast.title,
                message: translate.successImportFilesToast.message,
                type: 'success',
            });

            if (failedFiles.length > 0) {
                showToast({
                    title: translate.errorImportFilesToast.title,
                    message: translate.errorImportFilesToast.messageWithFiles.replace('{files}', failedFiles.join(', ')),
                    type: 'danger',
                });
            }

            resetForm();
            onClose(false);
        } catch (error) {
            console.error('Error importing files:', error);
            const message = error instanceof UploadError
                ? translate.errorImportFilesToast.messageWithFiles.replace('{files}', error.failedFiles.join(', '))
                : translate.errorImportFilesToast.message;
            showToast({
                title: translate.errorImportFilesToast.title,
                message,
                type: 'danger',
            });
        } finally {
            setIsLoading(false);
        }
    }, [
        tenantId,
        catalogId,
        onClose,
        resetForm,
        isLoading,
        translate,
        trackKnowledgeBaseEvent,
    ]);

    const handleUpdateDocument = useCallback(async () => {
        if (preProcessConfig.length === 0 || !documentToUpdateId || isLoading) {
            return;
        }

        try {
            setIsLoading(true);
            await KnowledgeBaseApi.createPreProcessedDocumentVersionAsync(tenantId, catalogId, documentToUpdateId, preProcessConfig[0]);

            trackKnowledgeBaseEvent(KNOWLEDGE_CONTENT_REFRESH, {
                knowcontName: preProcessConfig[0].file.name,
                knowcontType: preProcessConfig[0].file.name.split('.').pop() ?? 'unknown',
                knowcontSize: preProcessConfig[0].file.size,
                knowcontPreProcessingOptions: {
                    optimization: preProcessConfig[0].optimization,
                    indexing: preProcessConfig[0].indexing,
                    contextualization: preProcessConfig[0].contextualization,
                },
            });

            showToast({
                title: translate.successUpdateFileToast.title,
                message: translate.successUpdateFileToast.message,
                type: 'success',
            });

            resetForm();
            onClose(false);
        } catch (error) {
            console.error('Error updating file:', error);
            showToast({
                title: translate.errorUpdateFileToast.title,
                message: translate.errorUpdateFileToast.message,
                type: 'danger',
            });
        } finally {
            setIsLoading(false);
        }
    }, [
        tenantId,
        catalogId,
        onClose,
        resetForm,
        isLoading,
        translate,
        trackKnowledgeBaseEvent,
    ]);

    return (
        <BdsSidebar onBdsToggle={handleSidebarClose} isOpen={isOpen} type="over" sidebarPosition="right" width={761} background="surface-0">
            <div slot="header">
                <PageHeader title={translate.title} subtitle={translate.subtitle} />
            </div>
            <BdsGrid gap="1" direction="column" slot="body">
                <UploadWrapper disabled={isLoading}>
                    <BdsUpload
                        language={languageCode}
                        titleName={translate.fileUpload.title}
                        subtitle={translate.fileUpload.subtitle}
                        dataAccept={ACCEPTED_FILE_TYPES}
                        multiple={!documentToUpdateId}
                        onBdsUploadChange={handleFileUpload}
                        showListPreview={false}
                        ref={uploadRef}
                    />
                </UploadWrapper>
                <BdsBanner context="inside" variant="system">
                    <BdsTypo variant="fs-14" bold="bold">{translate.preProcessBanner}</BdsTypo>
                </BdsBanner>
                {preProcessConfig.length > 0 && preProcessConfig.map((config, index) => (
                    <DocumentAccordion key={config.file.name} config={config} isOpen={index === 0} isGroundingPreprocessingEnabled={isGroundingPreprocessingEnabled} disabled={isLoading} onDelete={handleDeleteDocument} onTogglePreProcess={handleTogglePreProcess} />
                ))}
            </BdsGrid>
            <FooterWrapper slot="footer">
                <ModalFooter cancelText={translate.cancelButton}
                    saveText={translate.saveButton}
                    isLoading={isLoading}
                    isFormValid={preProcessConfig.length > 0}
                    onCancel={handleCancel}
                    onSave={documentToUpdateId ? handleUpdateDocument : handleImportFiles} />
            </FooterWrapper>
        </BdsSidebar>
    );
};

export default UploadFileSidebar;
